/** PROTOTYPE config drawer -- THROWAWAY. Generic: search + common cluster + collapsible groups. Value source injected. */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { ModuleKey, MODULE_LABELS } from './spreadSplitData';
import { MODULE_CONFIG_GROUPS, ConfigField } from './spreadConfigDefs';

export interface ConfigDrawerProps {
  title: string;
  subtitle: string;
  moduleKey: ModuleKey;
  band?: string | null;               // welding discount band to show; null/undefined = show all
  getValue: (key: string) => number;
  getDefault: (key: string) => number;
  isOverridden: (key: string) => boolean;
  setCfg: (key: string, v: number | undefined) => void;
  resetAll: () => void;
  resetLabel: string;
  onClose: () => void;
}

const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 14px' };

const Field: React.FC<{ field: ConfigField; value: number; def: number; overridden: boolean; onChange: (v: number) => void; onReset: () => void }> = ({ field, value, def, overridden, onChange, onReset }) => (
  <div>
    <div style={{ fontSize: 10.5, fontWeight: 600, color: CRC.heritageGrey, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
      {field.label}{field.suffix === '%' ? ' %' : field.suffix === 'x' ? ' x' : ''}
    </div>
    {field.desc && <div style={{ fontSize: 10.5, color: CRC.heritageGrey, marginBottom: 5, lineHeight: 1.35 }}>{field.desc}</div>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {field.suffix === '$' && <span style={{ color: CRC.heritageGrey, fontSize: 12 }}>$</span>}
      <input title={field.label} type="number" value={Number.isFinite(value) ? value : 0} onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: 96, padding: '7px 9px', fontFamily: 'Funnel Sans, sans-serif', fontSize: 13, fontWeight: 600, borderRadius: 8, color: CRC.blueprintBlue,
          border: `1.5px solid ${overridden ? CRC.heroRed : CRC.border}`, background: overridden ? CRC.heroRed10 : '#fff' }} />
    </div>
    <div style={{ marginTop: 3, fontSize: 10.5, color: CRC.heritageGrey }}>
      default {def}
      {overridden && <button onClick={onReset} style={{ marginLeft: 8, fontSize: 10.5, color: CRC.readableRed, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>reset</button>}
    </div>
  </div>
);

export const ConfigDrawer: React.FC<ConfigDrawerProps> = ({ title, subtitle, moduleKey, band, getValue, getDefault, isOverridden, setCfg, resetAll, resetLabel, onClose }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const groups = MODULE_CONFIG_GROUPS[moduleKey];

  const groupFields = (g: typeof groups[number]) => (g.fields.some((fd) => fd.band) && band ? g.fields.filter((fd) => fd.band === band) : g.fields);
  const allVisible = groups.flatMap(groupFields);
  const commonFields = allVisible.filter((fd) => fd.common);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const matches = allVisible.filter((fd) => fd.label.toLowerCase().includes(q) || fd.desc?.toLowerCase().includes(q));

  const toggle = (t: string) => setOpen((prev) => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const render = (fd: ConfigField) => (
    <Field key={fd.key} field={fd} value={getValue(fd.key)} def={getDefault(fd.key)} overridden={isOverridden(fd.key)} onChange={(v) => setCfg(fd.key, v)} onReset={() => setCfg(fd.key, undefined)} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,39,63,0.25)' }} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 440, background: '#fff', boxShadow: '-8px 0 24px rgba(0,39,63,0.12)', display: 'flex', flexDirection: 'column', fontFamily: 'Montserrat, sans-serif' }}>
        <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${CRC.border}` }}>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 16, fontWeight: 600, color: CRC.blueprintBlue }}>{title}</div>
          <div style={{ fontSize: 11.5, color: CRC.heritageGrey, marginTop: 3, marginBottom: 12 }}>{subtitle}</div>
          <input title="Filter settings" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="🔍  Filter settings…"
            style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 8, border: `1px solid ${CRC.border}`, color: CRC.textGrey, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          {searching ? (
            matches.length ? <div style={grid}>{matches.map(render)}</div> : <div style={{ fontSize: 13, color: CRC.heritageGrey }}>No settings match “{query}”.</div>
          ) : (
            <>
              {commonFields.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: 'Funnel Sans', fontSize: 13, fontWeight: 600, color: CRC.blueprintBlue, marginBottom: 3 }}>Common</div>
                  <div style={{ fontSize: 10.5, color: CRC.heritageGrey, marginBottom: 10 }}>The knobs most people change. Everything else is grouped below.</div>
                  <div style={grid}>{commonFields.map(render)}</div>
                </div>
              )}
              <div style={{ fontSize: 10, fontWeight: 700, color: CRC.heritageGrey, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>All settings</div>
              {groups.map((g) => {
                const fields = groupFields(g);
                const isOpen = open.has(g.title);
                return (
                  <div key={g.title} style={{ borderBottom: `1px solid ${CRC.rowDivider}` }}>
                    <button onClick={() => toggle(g.title)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '11px 2px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: CRC.blueprintBlue }}>{g.title}</span>
                      <span style={{ fontSize: 11, color: CRC.heritageGrey }}>{fields.length} · {isOpen ? '▾' : '▸'}</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: '2px 2px 16px' }}>
                        {g.note && <div style={{ fontSize: 11, color: CRC.readableRed, marginBottom: 10 }}>{g.note}</div>}
                        <div style={grid}>{fields.map(render)}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${CRC.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={resetAll} style={{ fontSize: 12.5, color: CRC.readableRed, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{resetLabel}</button>
          <button onClick={onClose} style={{ padding: '8px 18px', background: CRC.heroRed, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
};
